import { useState } from "react";
import Swal from "sweetalert2";
import { API_BASE, PINPAD_URL } from "../config";
import { asegurarVersionVigente } from "../utilities/versionApp";
import { apiFetch } from "../utilities/apiFetch";

const useValidacionPago = (cajeroId, alumnoSeleccionado) => {
  const [referenciasPendientes, setReferenciasPendientes] = useState([]);
  const [cargandoReferencias, setCargandoReferencias] = useState(false);
  const [mostrarModalValidacion, setMostrarModalValidacion] = useState(false);
  const [refSeleccionada, setRefSeleccionada] = useState(null);
  const [folioBanco, setFolioBanco] = useState("");
  const [bancoSeleccionado, setBancoSeleccionado] = useState("");
  const [loadingValidacion, setLoadingValidacion] = useState(false);

  // Modo del modal: "validar" (normal) | "complemento" (cobrar el faltante)
  const [modoModal, setModoModal] = useState("validar");

  const rolCajero = String(localStorage.getItem("cajero_rol") || "");
  const tienePinpad = rolCajero === "2";

  const buscarReferenciasPendientes = async (matricula) => {
    setCargandoReferencias(true);
    try {
      const respuesta = await apiFetch(`${API_BASE}/wMultipagosObtenerPendientes.php?matricula=${matricula}`);
      const resultado = await respuesta.json();
      setReferenciasPendientes(resultado.success ? resultado.data : []);
    } catch (error) {
      console.error("Error al buscar referencias:", error);
      Swal.fire("Error de conexión", "Hubo un error al conectar con el servidor.", "error");
    } finally {
      setCargandoReferencias(false);
    }
  };

  const refrescar = () => {
    if (alumnoSeleccionado) buscarReferenciasPendientes(alumnoSeleccionado.matricula);
  };

  // --- VALIDACIÓN NORMAL (y segundo voucher de ventanilla) ---
  const confirmarValidacionPago = async (discrepancia = null) => {
    if (!folioBanco.trim() || !bancoSeleccionado) {
      Swal.fire("Atención", "Selecciona dónde se realizó el pago e ingresa el folio.", "warning");
      return;
    }

    setLoadingValidacion(true);
    try {
      // Si la referencia ya está PARCIAL, este pago es un complemento
      const esComplemento = refSeleccionada?.estatus === "PARCIAL";

      let url, payload;

      if (esComplemento) {
        url = `${API_BASE}/wMultipagosCobrarComplemento.php`;
        payload = {
          id_intencion_origen: refSeleccionada.id,
          id_cajero: cajeroId,
          folio_autorizacion: folioBanco,
          id_origen: 1,
          importe: calcularFaltante(refSeleccionada)
        };
      } else {
        url = `${API_BASE}/wMultipagosValidarPago.php`;
        payload = {
          id_intencion: refSeleccionada.id,
          id_cajero: cajeroId,
          folio_banco: folioBanco,
          metodo_pago: bancoSeleccionado,
          discrepancia: discrepancia
        };
      }

      const respuesta = await apiFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const resultado = await respuesta.json();

      if (resultado.success) {
        Swal.fire({
          title: resultado.codigo === "PARCIAL" ? "Abono registrado" : "¡Pago Validado!",
          text: resultado.message,
          icon: resultado.codigo === "PARCIAL" ? "info" : "success",
          confirmButtonText: "Aceptar",
          confirmButtonColor: "#28a745"
        });

        cerrarModal();
        refrescar();
      } else {
        Swal.fire("No se pudo validar", resultado.message || "Ocurrió un problema.", "error");
      }
    } catch (error) {
      console.error("Error al validar el pago:", error);
      Swal.fire("Error crítico", "Hubo un error al procesar la validación.", "error");
    } finally {
      setLoadingValidacion(false);
    }
  };

  // --- COBRO DEL FALTANTE CON PINPAD ---
  const cobrarFaltantePinpad = async (referencia) => {
    const faltante = calcularFaltante(referencia);
    if (faltante <= 0) return;

    setLoadingValidacion(true);
    try {
      const refBase = (referencia.referencia_banorte || "").substring(0, 26);
      const referenciaTerminal = refBase + Date.now().toString().slice(-4);

      // Antes de pasar la tarjeta: si hay una version nueva publicada, no cobrar
      if (!(await asegurarVersionVigente())) return;

      const resPinpad = await fetch(PINPAD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Monto: faltante,
          Referencia: referenciaTerminal,
          Concepto: "COMPLEMENTO DE PAGO"
        })
      });
      const dataPinpad = await resPinpad.json();

      if (dataPinpad.status !== "Aprobado") {
        Swal.fire("Pago Declinado", `La terminal reportó: ${dataPinpad.mensaje}`, "error");
        return;
      }

      await registrarComplemento(referencia, dataPinpad.autorizacion, 2, faltante, true);
    } catch (error) {
      console.error("Error con la Pinpad:", error);
      Swal.fire("Error de Terminal", "No se pudo conectar con la Pinpad. Verifica que la aplicación local esté abierta.", "error");
    } finally {
      setLoadingValidacion(false);
    }
  };

  // --- COBRO DEL FALTANTE CON TPV (el cajero captura la autorización) ---
  const cobrarFaltanteTPV = async (referencia, folioAutorizacion) => {
    const faltante = calcularFaltante(referencia);
    if (faltante <= 0) return;

    setLoadingValidacion(true);
    try {
      await registrarComplemento(referencia, folioAutorizacion, 3, faltante, false);
    } finally {
      setLoadingValidacion(false);
    }
  };

  // --- REGISTRO DEL COMPLEMENTO EN LA BD ---
  const registrarComplemento = async (referencia, folioAutorizacion, idOrigen, importe, avisarSiFalla) => {
    try {
      const respuesta = await apiFetch(`${API_BASE}/wMultipagosCobrarComplemento.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_intencion_origen: referencia.id,
          id_cajero: cajeroId,
          folio_autorizacion: folioAutorizacion,
          id_origen: idOrigen,
          importe: importe
        })
      });
      const resultado = await respuesta.json();

      if (resultado.success) {
        Swal.fire({
          title: "¡Pago Completado!",
          text: resultado.message,
          icon: "success",
          confirmButtonColor: "#28a745"
        });
        cerrarModal();
        refrescar();
      } else if (avisarSiFalla) {
        // El dinero ya se cobró en la terminal pero no se guardó
        Swal.fire(
          "Atención: cobro retenido",
          `El pago SÍ pasó en la terminal (autorización ${folioAutorizacion}), pero no se guardó en el sistema: ${resultado.message}`,
          "warning"
        );
      } else {
        Swal.fire("No se pudo registrar", resultado.message, "error");
      }
    } catch (error) {
      console.error("Error al registrar complemento:", error);
      Swal.fire("Error crítico", "No se pudo registrar el complemento.", "error");
    }
  };

  const calcularFaltante = (referencia) => {
    if (!referencia) return 0;
    const total = parseFloat(referencia.importe_total || 0);
    const abonado = parseFloat(referencia.abonado || 0);
    return +(total - abonado).toFixed(2);
  };

  const abrirModalValidacion = (referencia) => {
    setRefSeleccionada(referencia);
    setFolioBanco("");
    setBancoSeleccionado(referencia?.estatus === "PARCIAL" ? "2" : "");
    setModoModal("validar");
    setMostrarModalValidacion(true);
  };

  const abrirModalComplemento = (referencia) => {
    setRefSeleccionada(referencia);
    setFolioBanco("");
    setBancoSeleccionado("");
    setModoModal("complemento");
    setMostrarModalValidacion(true);
  };

  const cerrarModal = () => {
    setMostrarModalValidacion(false);
    setRefSeleccionada(null);
    setFolioBanco("");
    setBancoSeleccionado("");
    setModoModal("validar");
  };

  return {
    referenciasPendientes,
    cargandoReferencias,
    mostrarModalValidacion,
    setMostrarModalValidacion,
    refSeleccionada,
    folioBanco,
    setFolioBanco,
    buscarReferenciasPendientes,
    confirmarValidacionPago,
    abrirModalValidacion,
    abrirModalComplemento,
    bancoSeleccionado,
    setBancoSeleccionado,
    loadingValidacion,
    modoModal,
    cerrarModal,
    cobrarFaltantePinpad,
    cobrarFaltanteTPV,
    calcularFaltante,
    tienePinpad
  };
};

export default useValidacionPago;